import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils";

const inputVariants = cva(
  "qodo qodo-input file:text-foreground placeholder:text-text-general-neutral-muted selection:bg-primary selection:text-primary-foreground border-input-neutral-idle flex h-9 w-full min-w-0 rounded-md border bg-fill-input-neutral-idle text-text-general-neutral-strong px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-none focus-visible:ring-none aria-invalid:border-border-input-negative-idle aria-invalid:rounded-[6px_var(--border-radius-md,6px)_6px_6px] disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-fill-input-neutral-disabled disabled:border-border-input-neutral-disabled disabled:text-text-input-neutral-disabled read-only:cursor-default md:text-sm [[readonly]]:bg-fill-input-neutral-idle [[readonly]]:border-0 [[readonly]]:text-text-input-neutral-idle [[readonly]]:hover:bg-fill-input-neutral-idle [[readonly]]:hover:border-border-input-neutral-idle [[readonly]]:focus:bg-fill-input-neutral-idle [[readonly]]:focus:border-border-input-neutral-idle [[readonly]]:focus-visible:bg-fill-input-neutral-idle [[readonly]]:focus-visible:border-border-input-neutral-idle [[readonly][disabled]]:opacity-100 [[readonly][disabled]]:cursor-default [[readonly][disabled]]:bg-fill-input-neutral-idle [[readonly][disabled]]:border-0 [[readonly][disabled]]:text-text-input-neutral-idle [[readonly][disabled]]:hover:bg-fill-input-neutral-idle [[readonly][disabled]]:hover:border-0 [[readonly][disabled]]:focus:bg-fill-input-neutral-idle [[readonly][disabled]]:focus:border-0 [[readonly][disabled]]:focus-visible:bg-fill-input-neutral-idle [[readonly][disabled]]:focus-visible:border-0",
  {
    variants: {
      variant: {
        default:
          "border-border-input-neutral-idle hover:bg-fill-input-neutral-hover hover:border-border-input-neutral-hover focus:bg-fill-input-neutral-idle focus:border-border-input-emphasis-active focus:rounded-[6px_var(--border-radius-md,6px)_6px_6px] focus:text-text-input-neutral-active selection:bg-fill-input-emphasis-idle selection:text-text-input-neutral-active focus-visible:ring-none focus-visible:ring-none [[readonly]]:hover:bg-fill-input-neutral-idle [[readonly]]:hover:border-0 [[readonly]]:focus:bg-fill-input-neutral-idle [[readonly]]:focus:border-0 [[readonly]]:focus-visible:bg-fill-input-neutral-idle [[readonly]]:focus-visible:border-0",
        secondary:
          "bg-secondary border-secondary text-secondary-foreground placeholder:text-purple-muted-400",
        accent:
          "bg-accent border-accent text-accent-foreground placeholder:text-purple-muted-400",
        success:
          "bg-success border-success text-success-foreground placeholder:text-success-foreground/70",
        warning:
          "bg-warning border-warning text-warning-foreground placeholder:text-warning-foreground/70",
        destructive:
          "border-destructive bg-fill-input-neutral-idle text-text-input-neutral-idle placeholder:text-destructive-foreground/70",
        ghost:
          "bg-transparent border-border-input-neutral-idle focus-visible:ring-none focus-visible:ring-none",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface InputProps
  extends React.ComponentProps<"input">,
    VariantProps<typeof inputVariants> {
  error?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, type, error, startIcon, endIcon, ...props }, ref) => {
    const hasStartIcon = !!startIcon;
    const hasEndIcon = !!endIcon;

    const inputEl = (
      <input
        ref={ref}
        type={type}
        data-slot="input"
        aria-invalid={error || undefined}
        className={cn(
          inputVariants({ variant }),
          hasStartIcon && "pl-9",
          hasEndIcon && "pr-9",
          className,
        )}
        {...props}
      />
    );

    if (!hasStartIcon && !hasEndIcon) {
      return inputEl;
    }

    const renderIconIfNeeded = (
      icon: React.ReactNode,
      side: "left" | "right",
    ) => {
      if (!icon) return <></>;

      return (
        <div
          className={cn(
            "pointer-events-none absolute top-1/2 -translate-y-1/2 flex items-center [&>svg]:size-4 [&>svg]:shrink-0",
            side === "left" ? "left-3" : "right-3",
          )}
        >
          {icon}
        </div>
      );
    };

    return (
      <div className="relative">
        {renderIconIfNeeded(startIcon, "left")}
        {inputEl}
        {renderIconIfNeeded(endIcon, "right")}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
